'use client'

import { useRef, useState, useEffect } from 'react'
import { useInView, useScrollProgress } from '@/hooks/useInView'
import { AlertTriangle, Clock, DollarSign, TrendingDown } from 'lucide-react'

/* ── Animated counter ─────────────────────────────────────── */
function useCounter(end: number, duration = 2000, active = false) {
  const [value, setValue] = useState(end)
  useEffect(() => {
    if (!active) return
    const startTime = performance.now()
    function tick(now: number) {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - (1 - progress) * (1 - progress)
      setValue(Math.round(end * eased))
      if (progress < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [end, duration, active])
  return value
}

const PROBLEMS = [
  {
    icon: AlertTriangle,
    title: 'Disconnected Apps',
    description:
      "Roofr handles quotes. Another app covers photos. But your CRM, invoicing, and communication tools still don't sync. You spend hours chasing data instead of running your business.",
    stat: '8+',
    statLabel: 'disconnected tools the average contractor juggles daily',
    color: 'from-orange-500/20 to-orange-600/5',
    borderColor: 'group-hover:border-orange-500/30',
  },
  {
    icon: Clock,
    title: 'Missed Follow-ups',
    description:
      "Every missed call or late reply is a job gone. Follow-ups rarely happen when you're buried in admin work. Each \"maybe later\" becomes someone else's payday.",
    stat: '67%',
    statLabel: 'of new leads never receive a single follow-up',
    color: 'from-yellow-500/20 to-yellow-600/5',
    borderColor: 'group-hover:border-yellow-500/30',
  },
  {
    icon: DollarSign,
    title: 'Lost Revenue',
    description:
      "Disorganization bleeds profit. Every delay, gap, or forgotten follow-up leaks money. When your systems don't talk, you pay for the silence.",
    stat: '$47K+',
    statLabel: 'lost per sales rep each year from missed follow-ups',
    color: 'from-red-500/20 to-red-600/5',
    borderColor: 'group-hover:border-red-500/30',
  },
  {
    icon: TrendingDown,
    title: 'Falling Behind',
    description:
      "Your competition isn't smarter — they're faster. While you chase paperwork, they close deals. Speed isn't a luxury; it's the only thing keeping you in the game.",
    stat: '73%',
    statLabel: 'more jobs closed by speed-driven teams each month',
    color: 'from-rose-500/20 to-rose-600/5',
    borderColor: 'group-hover:border-rose-500/30',
  },
]

export function ProblemSection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const { ref: inViewRef, isInView } = useInView()

  const progress = useScrollProgress(sectionRef)

  // Map scroll progress to bg opacity: [0, 0.3, 0.7, 1] -> [0, 0.5, 0.5, 0]
  let bgOpacity: number
  if (progress < 0.3) {
    bgOpacity = (progress / 0.3) * 0.5
  } else if (progress < 0.7) {
    bgOpacity = 0.5
  } else {
    bgOpacity = ((1 - progress) / 0.3) * 0.5
  }

  return (
    <section ref={sectionRef} className="section relative overflow-hidden">
      {/* Animated background glow */}
      <div
        style={{ opacity: bgOpacity }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-red-600/[0.04] blur-[120px]" />
      </div>

      <div ref={inViewRef} className="section-inner relative z-10">
        {/* Section Header */}
        <div
          className={`text-center mb-16 sm:mb-20 anim-fade-in-up ${isInView ? 'in-view' : ''}`}
        >
          <p
            className={`text-xs uppercase tracking-[0.2em] text-red-500 font-semibold mb-4 anim-fade-in-up ${isInView ? 'in-view' : ''}`}
          >
            The Problem
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            Running Your Business{' '}
            <span className="gradient-text-red">
              Shouldn&apos;t Feel Like a Circus Act.
            </span>
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 max-w-3xl mx-auto leading-relaxed">
            Right now, you&apos;re juggling quotes, jobs, photos, and follow-ups
            across five different tools... and every drop costs you time and
            closed jobs.
          </p>
        </div>

        {/* Problem Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-5xl mx-auto">
          {PROBLEMS.map((problem, i) => {
            const Icon = problem.icon
            return (
              <ProblemCard
                key={problem.title}
                problem={problem}
                icon={Icon}
                index={i}
                isInView={isInView}
              />
            )
          })}
        </div>

        {/* Transition text */}
        <div
          className={`text-center mt-16 anim-fade-in-up anim-delay-6 ${isInView ? 'in-view' : ''}`}
        >
          <p className="text-lg sm:text-xl text-zinc-400">
            There&apos;s a better way.{' '}
            <span className="text-white font-semibold">One system to run it all.</span>
          </p>
        </div>
      </div>
    </section>
  )
}

/* ── Individual Card ──────────────────────────────────────── */
function ProblemCard({
  problem,
  icon: Icon,
  index,
  isInView,
}: {
  problem: (typeof PROBLEMS)[number]
  icon: typeof AlertTriangle
  index: number
  isInView: boolean
}) {
  const statNum = parseInt(problem.stat.replace(/[^0-9]/g, ''))
  const statPrefix = problem.stat.match(/^\$/)?.[0] || ''
  const statSuffix = problem.stat.match(/[%+K]+$/)?.[0] || ''
  const animatedValue = useCounter(statNum, 2000, isInView)

  return (
    <div
      className={`group glass-card-premium p-6 sm:p-8 transition-all duration-500 ${problem.borderColor} anim-fade-in-up anim-delay-${index + 2} ${isInView ? 'in-view' : ''}`}
    >
      <div className="flex items-start gap-4 sm:gap-5">
        {/* Icon */}
        <div
          className={`shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br ${problem.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-500`}
        >
          <Icon className="text-red-500" size={22} />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-2 group-hover:text-red-400 transition-colors duration-300">
            {problem.title}
          </h3>
          <p className="text-sm text-zinc-400 leading-relaxed mb-4">
            {problem.description}
          </p>

          {/* Animated stat */}
          <div className="flex items-baseline gap-2 bg-zinc-900/60 rounded-lg p-3 border border-zinc-800/50">
            <span className="text-2xl sm:text-3xl font-extrabold text-red-500 tabular-nums">
              {statPrefix}
              {animatedValue.toLocaleString()}
              {statSuffix}
            </span>
            <span className="text-xs text-zinc-500 leading-snug">
              {problem.statLabel}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
